import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import { API, auth, Modal, errMsg, fmtDate } from '../finance/shared';
import Icon from '../../../components/dashboard/DashboardIcons';

const COACH_TYPE_LABELS = { head_coach: 'مدرب رئيسي', assistant_coach: 'مدرب مساعد', goalkeeper_coach: 'مدرب مرمى', fitness_coach: 'مدرب أعداد بدني', analyst: 'محلل فني', physiotherapist: 'أخصائي علاج طبيعي', other: 'أخرى' };
const AVAILABILITY_LABELS = { available: 'متاح للتعاقد', not_available: 'غير متاح حالياً', open_to_offers: 'مهتم بالعروض المناسبة' };
const EMPLOYMENT_LABELS = { full_time: 'دوام كامل', part_time: 'دوام جزئي', freelance: 'مستقل / بالساعة' };
const CERT_STATUS_LABELS = { verified: 'موثّقة', unverified: 'غير موثّقة', rejected: 'مرفوضة' };
const CERT_STATUS_COLORS = { verified: 'bg-teal-50 text-teal-700', unverified: 'bg-slate-100 text-slate-500', rejected: 'bg-red-50 text-red-600' };

/** نافذة السيرة الذاتية الكاملة للمدرب — تُستخدم من كل تبويبات الاستقطاب (بحث/مرشحون/عروض/طاقم) */
export default function CoachProfileModal({ coachId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!coachId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/coaches/org-view/${coachId}`, auth());
      setProfile(res.data);
    } catch (err) { toast.error(errMsg(err, 'تعذّر تحميل السيرة الذاتية')); onClose(); }
    setLoading(false);
  }, [coachId, onClose]);
  useEffect(() => { load(); }, [load]);

  const downloadCv = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/coaches/documents/${profile.cv_document_id}`, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `سيرة-${profile.full_name_ar || 'مدرب'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errMsg(err, 'تعذّر تحميل الملف')); }
    setDownloading(false);
  };

  return (
    <Modal open={Boolean(coachId)} title="السيرة الذاتية للمدرب" onClose={onClose} wide>
      {loading || !profile ? (
        <p className="py-10 text-center text-sm text-slate-400" data-testid="coach-profile-loading">جارِ التحميل...</p>
      ) : (
        <div className="space-y-5" data-testid="coach-profile-modal">
          <div className="flex items-start gap-4">
            {profile.photo_file_id ? (
              <img src={`${API}/files/${profile.photo_file_id}`} alt={profile.full_name_ar} className="h-16 w-16 shrink-0 rounded-full object-cover" />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100 text-lg font-bold text-slate-400">{profile.full_name_ar?.[0] || '؟'}</div>
            )}
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-slate-800" data-testid="coach-profile-name">{profile.full_name_ar}</h3>
              <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                <span className="rounded-full bg-[#16233b]/5 px-2 py-0.5 font-semibold text-[#16233b]">{COACH_TYPE_LABELS[profile.coach_type] || profile.custom_specialty}</span>
                {profile.years_experience != null && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{profile.years_experience} سنوات خبرة</span>}
                {profile.city && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{profile.city}</span>}
                {profile.availability_status && <span className="rounded-full bg-teal-50 px-2 py-0.5 font-semibold text-teal-700">{AVAILABILITY_LABELS[profile.availability_status] || profile.availability_status}</span>}
              </div>
            </div>
          </div>

          {profile.bio && (
            <div>
              <h4 className="mb-1 text-xs font-bold text-slate-500">نبذة</h4>
              <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700" data-testid="coach-profile-bio">{profile.bio}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            {profile.employment_type && <div><span className="block text-[11px] text-slate-400">نوع التعاقد المفضّل</span><span className="font-semibold text-slate-700">{EMPLOYMENT_LABELS[profile.employment_type] || profile.employment_type}</span></div>}
            {profile.languages?.length > 0 && <div><span className="block text-[11px] text-slate-400">اللغات</span><span className="font-semibold text-slate-700">{profile.languages.join('، ')}</span></div>}
            {(profile.expected_salary_min || profile.expected_salary_max) && <div><span className="block text-[11px] text-slate-400">الراتب المتوقع</span><span className="font-semibold text-slate-700">{profile.expected_salary_min || '—'} - {profile.expected_salary_max || '—'} ريال</span></div>}
          </div>

          {profile.skills?.length > 0 && (
            <div>
              <h4 className="mb-1.5 text-xs font-bold text-slate-500">المهارات</h4>
              <div className="flex flex-wrap gap-1.5">
                {profile.skills.map((s, i) => <span key={i} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600">{s}</span>)}
              </div>
            </div>
          )}

          {profile.experiences?.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-bold text-slate-500">الخبرات العملية</h4>
              <div className="space-y-2.5" data-testid="coach-profile-experiences">
                {profile.experiences.map((exp) => (
                  <div key={exp.id} className="flex gap-2.5 rounded-lg border border-slate-100 p-2.5">
                    <Icon name="briefcase" className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800">{exp.title} — {exp.organization_name}</div>
                      <div className="text-[11px] text-slate-400">{fmtDate(exp.start_date)} → {exp.end_date ? fmtDate(exp.end_date) : 'حتى الآن'}</div>
                      {exp.description && <p className="mt-1 text-xs text-slate-600">{exp.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {profile.certifications?.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-bold text-slate-500">الشهادات والرخص</h4>
              <div className="space-y-2" data-testid="coach-profile-certifications">
                {profile.certifications.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-2.5">
                    <div className="flex items-center gap-2">
                      <Icon name="star" className="h-4 w-4 shrink-0 text-orange-400" />
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{c.name}{c.level ? ` — ${c.level}` : ''}</div>
                        <div className="text-[11px] text-slate-400">{c.issuing_authority}</div>
                      </div>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${CERT_STATUS_COLORS[c.verification_status] || 'bg-slate-100 text-slate-500'}`}>{CERT_STATUS_LABELS[c.verification_status] || c.verification_status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            {profile.cv_document_id && (
              <button onClick={downloadCv} disabled={downloading} className="flex items-center gap-1.5 rounded-lg bg-[#16233b] px-3.5 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="coach-profile-download-cv-btn">
                <Icon name="download" className="h-4 w-4" /> {downloading ? 'جارِ التحميل...' : 'تحميل السيرة الذاتية (PDF)'}
              </button>
            )}
            {profile.intro_video_url && (
              <a href={profile.intro_video_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-600" data-testid="coach-profile-video-link">
                <Icon name="expand" className="h-4 w-4" /> مقطع تعريفي
              </a>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
